/**
 * Locale-aware number input utilities.
 *
 * English (en): decimal = ".", thousands = ","
 * Spanish/others (es, pl, ...): decimal = ",", thousands = "."
 */

interface LocaleSeparators {
  decimal: string
  thousands: string
}

type ValueParam = string | number | null | undefined | ""

export const getLocaleSeparators = (locale: string): LocaleSeparators => {
  if (locale === "en") return { decimal: ".", thousands: "," }
  return { decimal: ",", thousands: "." }
}

/**
 * Validates whether a string is a well-formed locale number.
 * Allows: optional leading minus, digits, thousands separators, at most one decimal separator,
 * and scientific notation (e.g. 1e20, 1e-20, 6.578549071879811e-06).
 * Rejects standalone minus signs and trailing decimal separators (e.g. "-", "12," or "12.").
 */
export const isValidLocaleNumber = (value: string, locale: string): boolean => {
  if (value === "") return true
  const { decimal, thousands } = getLocaleSeparators(locale)
  const escapedDec = decimal === "." ? "\\." : ","
  const escapedTh = thousands === "." ? "\\." : ","
  const exp = `([eE][+-]?\\d+)?`
  const pattern = new RegExp(
    `^-?\\d{1,3}(${escapedTh}\\d{3})*(${escapedDec}\\d+)?${exp}$|^-?\\d+(${escapedDec}\\d+)?${exp}$`,
  )
  return pattern.test(value)
}

const isInvalidValue = (value: ValueParam): boolean =>
  value === null || value === undefined || value === ""

/**
 * Parses a locale-formatted number/string into a float.
 * Strips thousands separators and replaces the locale decimal with ".".
 */
export type LocaleNumberToStandard = (value: ValueParam, locale: string) => number | null
export type LocaleNumberToStandardFn = (
  value: ValueParam,
) => ReturnType<LocaleNumberToStandard>
export const localeNumberToStandard: LocaleNumberToStandard = (value, locale) => {
  if (isInvalidValue(value)) return null

  const val = String(value)
  const { decimal, thousands } = getLocaleSeparators(locale)
  const stripped = val.replaceAll(thousands, "")
  const normalized = decimal === "," ? stripped.replace(",", ".") : stripped
  const parsed = parseFloat(normalized)
  return isNaN(parsed) ? null : parsed
}

/**
 * Formats a number to a locale-aware string for display.
 * Replaces the standard decimal "." with the locale-appropriate separator.
 */
export type NumberToLocale = (value: ValueParam, locale: string) => string | null
export type NumberToLocaleFn = (value: ValueParam) => ReturnType<NumberToLocale>
export const numberToLocale: NumberToLocale = (value, locale) => {
  if (isInvalidValue(value)) return null

  const val = String(value)
  const { decimal } = getLocaleSeparators(locale)
  return val.replace(".", decimal)
}
